import { storage } from '@/utils/storage';
import { locationAPI } from '@/lib/api';
import {
    detectUserLocation,
    getBrowserPosition,
    resolveLocationFromCoordinates,
    saveUserLocation,
    type UserLocation,
} from '@/lib/location';

export type SearchCountryScope = {
    code: string;
    name: string;
    source: 'gps' | 'manual' | 'auto';
};

const SCOPE_CODE_KEY = 'search_country_code';
const SCOPE_NAME_KEY = 'search_country_name';
const SCOPE_SOURCE_KEY = 'search_country_source';
const SCOPE_SET_KEY = 'search_country_scope_set';

export function hasSearchCountryScope(): boolean {
    return storage.local.getItem(SCOPE_SET_KEY) === 'true'
        && Boolean(storage.local.getItem(SCOPE_CODE_KEY)?.trim());
}

export function getSearchCountryScope(): SearchCountryScope | null {
    if (!hasSearchCountryScope()) return null;
    const code = storage.local.getItem(SCOPE_CODE_KEY)?.trim().toUpperCase();
    const name = storage.local.getItem(SCOPE_NAME_KEY)?.trim();
    if (!code || !name) return null;
    const source = (storage.local.getItem(SCOPE_SOURCE_KEY) || 'manual') as SearchCountryScope['source'];
    return { code, name, source };
}

export function setSearchCountryScope(scope: SearchCountryScope): void {
    storage.local.setItem(SCOPE_CODE_KEY, scope.code.toUpperCase());
    storage.local.setItem(SCOPE_NAME_KEY, scope.name);
    storage.local.setItem(SCOPE_SOURCE_KEY, scope.source);
    storage.local.setItem(SCOPE_SET_KEY, 'true');
    storage.local.setItem('user_country_code', scope.code.toUpperCase());
    storage.local.setItem('user_country', scope.name);
}

export function clearSearchCountryScope(): void {
    storage.local.removeItem(SCOPE_CODE_KEY);
    storage.local.removeItem(SCOPE_NAME_KEY);
    storage.local.removeItem(SCOPE_SOURCE_KEY);
    storage.local.removeItem(SCOPE_SET_KEY);
}

/** Apply GPS-detected country as the locked search scope. */
export function applySearchCountryFromLocation(location: UserLocation): void {
    if (!location.countryCode || !location.country) return;
    setSearchCountryScope({
        code: location.countryCode,
        name: location.country,
        source: 'gps',
    });
}

/** Payload for GenUI listing API calls. */
export function getSearchCountryPayload(): {
    search_country_code?: string;
    search_country_name?: string;
} {
    const scope = getSearchCountryScope();
    if (!scope) return {};
    return {
        search_country_code: scope.code,
        search_country_name: scope.name,
    };
}

/**
 * Browser GPS first (sets country + city when catalog-valid), else pick from supported countries.
 */
export async function ensureSearchCountryScope(): Promise<
    { ok: true; scope: SearchCountryScope } | { ok: false; error: string }
> {
    if (hasSearchCountryScope()) {
        const existing = getSearchCountryScope();
        if (existing) return { ok: true, scope: existing };
    }

    try {
        const coords = await getBrowserPosition();
        const resolved = await resolveLocationFromCoordinates(
            coords.lat,
            coords.lon,
            coords.accuracy,
        );
        if (resolved?.countryCode && resolved.country) {
            const scope: SearchCountryScope = {
                code: resolved.countryCode,
                name: resolved.country,
                source: 'gps',
            };
            setSearchCountryScope(scope);
            return { ok: true, scope };
        }
    } catch {
        /* fall through to manual country list */
    }

    return {
        ok: false,
        error: 'Allow location access or choose your country below to search neighborhoods and pincodes.',
    };
}

export async function loadSupportedCountries(): Promise<
    { name: string; code: string; state_count: number }[]
> {
    try {
        const data = await locationAPI.getSupportedCountries();
        return (data.countries || []).filter((c) => c?.name && c?.code);
    } catch {
        return [];
    }
}

export async function detectAndSetCountryScopeFromGps(): Promise<
    { ok: true; scope: SearchCountryScope; location: UserLocation } | { ok: false; error: string }
> {
    try {
        const location = await detectUserLocation();
        if (!location?.countryCode?.trim() || !location?.country?.trim()) {
            return { ok: false, error: 'Could not detect your country. Choose one from the list.' };
        }
        const code = location.countryCode.trim().toUpperCase();
        const countries = await loadSupportedCountries();
        const hit = countries.find((c) => c.code.toUpperCase() === code);
        const scope: SearchCountryScope = {
            code: hit?.code ?? code,
            name: hit?.name ?? location.country.trim(),
            source: 'gps',
        };
        setSearchCountryScope(scope);
        if (location.catalog_enabled !== false && location.city?.trim()) {
            saveUserLocation(location);
        }
        return { ok: true, scope, location };
    } catch {
        return {
            ok: false,
            error: 'Location access was denied. Choose your country from the list below.',
        };
    }
}
